import { useState, useEffect, useMemo } from 'react';
import { useAuth } from "../providers/AuthProvider";
import { useNavigate } from "react-router-dom";
import { loginFields } from "../constants/formFields";
import FormAction from "./FormAction";
import FormExtra from "./FormExtra";
import Input from "./Input";
import axios from "axios";
import { Config } from "../config";
import { useThemeProvider } from '../utils/ThemeContext';
import { jwtDecode } from 'jwt-decode';
import { useNotifications } from '../contexts/NotificationContext';

const fields=loginFields;
let fieldsState = {};
fields.forEach(field=>fieldsState[field.id]='');

const OTP_REVEAL_MS = 500;

export default function Login({setToastErrorOpen, setToastErrorMsg}){
    const [loginState,setLoginState]=useState(fieldsState);
    const [step, setStep] = useState('credentials'); // 'credentials' | '2fa'
    const [tempToken, setTempToken] = useState(null);
    const [totpCode, setTotpCode] = useState('');
    const [revealUntil, setRevealUntil] = useState([]); // timestamps per digit
    const [now, setNow] = useState(Date.now());
    const [submitting, setSubmitting] = useState(false);
    const { setToken } = useAuth();
    const navigate = useNavigate();
    const { forceUpdate } = useNotifications();

    const handleChange=(e)=>{
        setLoginState({...loginState,[e.target.id]:e.target.value})
    }

    const handleSubmit=(e)=>{
        e.preventDefault();
        if (step === 'credentials') {
            authenticateUser();
        } else {
            verify2FA();
        }
    }

    const { attribs } = useThemeProvider();

    const completeLogin = async (data) => {
        const token = data.token.access_token;
        const decodedToken = jwtDecode(token);
        setToken(token);

        const userinfo = { ...data.userinfo };

        localStorage.removeItem('user_avatar');
        if (userinfo.avatar) {
            localStorage.setItem('user_avatar', userinfo.avatar);
            userinfo.avatar = 'stored_separately';
        }

        localStorage.setItem('userinfo', JSON.stringify(userinfo));
        localStorage.setItem('role', JSON.stringify(decodedToken.role));
        localStorage.setItem('last_login', JSON.stringify(data.last_login));
        localStorage.setItem('last_ip', JSON.stringify(data.last_ip));

        await forceUpdate();

        navigate("/dashboard", { replace: true });
    };

    const authenticateUser = async () => {
        setSubmitting(true);
        try {
            const res = await axios.post(Config.APIBaseURL + '/auth/login', {
                username: loginState.username,
                password: loginState.password
            }, {withCredentials: true});

            if (res.data.requires_2fa && res.data.temp_token) {
                // Transition to 2FA code entry
                setTempToken(res.data.temp_token);
                setStep('2fa');
                setSubmitting(false);
                return;
            }

            if (res.data.token) {
                await completeLogin(res.data);
            } else {
                setToastErrorMsg(res.data.result || 'Login failed');
                setToastErrorOpen(true);
            }
        } catch (error) {
            console.error('Error during login:', error);
            setToastErrorMsg('Login failed. Please try again.');
            setToastErrorOpen(true);
        } finally {
            setSubmitting(false);
        }
    }

    const verify2FA = async () => {
        if (!totpCode || totpCode.length < 6) {
            setToastErrorMsg('Please enter the 6-digit code from your authenticator app.');
            setToastErrorOpen(true);
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post(Config.APIBaseURL + '/auth/verify-2fa', {
                temp_token: tempToken,
                code: totpCode
            }, {withCredentials: true});

            if (res.data.token) {
                await completeLogin(res.data);
            } else {
                setToastErrorMsg('Invalid verification code.');
                setToastErrorOpen(true);
            }
        } catch (error) {
            const detail = error.response?.data?.detail;
            setToastErrorMsg(typeof detail === 'string' ? detail : 'Invalid or expired code.');
            setToastErrorOpen(true);
            // If temp token is expired, reset to credentials step
            if (error.response?.status === 400 && typeof detail === 'string' && detail.includes('expired')) {
                setStep('credentials');
                setTempToken(null);
                setTotpCode('');
            }
        } finally {
            setSubmitting(false);
        }
    }

    const handleBack = () => {
        setStep('credentials');
        setTempToken(null);
        setTotpCode('');
        setRevealUntil([]);
    };

    // Tick `now` while there are digits still revealed so the mask animates
    useEffect(() => {
        if (step !== '2fa') return;
        const hasVisible = revealUntil.some(t => t > Date.now());
        if (!hasVisible) return;
        const id = setInterval(() => setNow(Date.now()), 80);
        return () => clearInterval(id);
    }, [revealUntil, step]);

    // What to render inside the input: recent digits visible, older ones masked
    const totpDisplay = useMemo(() => {
        return totpCode
            .split('')
            .map((ch, i) => (revealUntil[i] && revealUntil[i] > now ? ch : '*'))
            .join('');
    }, [totpCode, revealUntil, now]);

    const handleOtpKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (totpCode.length === 6) verify2FA();
            return;
        }
        if (e.key === 'Backspace') {
            e.preventDefault();
            setTotpCode(prev => prev.slice(0, -1));
            setRevealUntil(prev => prev.slice(0, -1));
            return;
        }
        if (/^[0-9]$/.test(e.key) && totpCode.length < 6) {
            e.preventDefault();
            setTotpCode(prev => prev + e.key);
            setRevealUntil(prev => [...prev, Date.now() + OTP_REVEAL_MS]);
        }
    };

    if (step === '2fa') {
        return (
            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                <div className="text-center">
                    <h2 className={`text-xl font-semibold text-gray-${attribs.txtgray}`}>
                        Two-Factor Authentication
                    </h2>
                    <p className={`mt-2 text-sm text-gray-${attribs.txtgray} opacity-75`}>
                        Enter the 6-digit code from your authenticator app
                    </p>
                </div>

                <div>
                    <label htmlFor="totp_code" className={`block text-sm font-medium text-gray-${attribs.txtgray} mb-2`}>
                        Verification code
                    </label>
                    <input
                        id="totp_code"
                        name="totp_code"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        required
                        value={totpDisplay}
                        onKeyDown={handleOtpKeyDown}
                        onChange={() => {}}
                        className={`appearance-none rounded-md relative block w-full px-4 py-3 border border-gray-300 placeholder-gray-500 text-gray-${attribs.txtgray} focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-center text-md tracking-widest`}
                        placeholder="000000"
                        autoFocus
                    />
                </div>

                <div className="flex flex-col space-y-3">
                    <button
                        type="submit"
                        disabled={submitting || totpCode.length !== 6}
                        className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? 'Verifying...' : 'Verify'}
                    </button>
                    <button
                        type="button"
                        onClick={handleBack}
                        className={`text-sm text-indigo-500 hover:text-indigo-600`}
                    >
                        &larr; Back to login
                    </button>
                </div>
            </form>
        );
    }

    return(
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        <div className="-space-y-px">
            {
                fields.map(field=>
                        <Input
                            key={field.id}
                            handleChange={handleChange}
                            value={loginState[field.id]}
                            labelText={field.labelText}
                            labelFor={field.labelFor}
                            id={field.id}
                            name={field.name}
                            type={field.type}
                            isRequired={field.isRequired}
                            placeholder={field.placeholder}
                            customClass={`text-gray-${attribs.txtgray}`}
                    />

                )
            }
        </div>

        <FormExtra/>
        <FormAction handleSubmit={handleSubmit} text="Login"/>

      </form>
    )
}
